import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  FileText,
  Network,
  MessageSquare,
  ArrowRight,
  Palette,
  Languages,
  LayoutGrid,
  FolderOpen,
  Sparkles,
  Eye,
  FolderPlus,
  ScrollText,
  Building2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { api } from '@/lib/api';
import { addWatchlistEntity } from '@/lib/watchlist-client';
import { useUiStore } from '@/stores/ui-store';
import { useCommandPaletteStore } from '@/stores/command-palette-store';
import { useAuth } from '@/components/providers/auth-provider';
import { cn } from '@/lib/utils';
import { EntityName } from '@/components/bespoke/entity-name';
import { CommandPaletteSuggestions } from '@/components/command-palette-suggestions';
import { ENTITY_NAME_FA } from '@/lib/entity-names';
import { t } from '@/lib/i18n';

/**
 * Static suggested prompts drawn from frontend spec §5.1 examples.
 * The idle (empty-query) state picks a rotating subset of 4 each time
 * the palette opens, giving users discoverable starting points.
 */
const SUGGESTED_PROMPTS = [
  'Show me new signals on Trita Parsi',
  'Draft a brief on the Alavi evidence chain',
  'Summarize what changed on IRGC yesterday',
  "Trace Francis Najafi's funding path",
  'Which bridges connect NIAC and Quincy Institute?',
  'Investigate the Alavi Foundation property transfers',
  'Open a case file on IRGC front companies',
];

/** Verbs that trigger action dispatch when they're the first token. */
const ACTION_VERBS = [
  'brief',
  'find',
  'compare',
  'show',
  'trace',
  'summarize',
  'investigate',
] as const;

type ActionVerb = (typeof ACTION_VERBS)[number];

/** Detect whether the query looks like a natural-language question. */
function looksLikeQuestion(q: string): boolean {
  if (q.length <= 6) return false;
  return /\?$|^(who|what|when|where|why|how|trace|show|list|compare|find|brief|summarize|investigate)\b/i.test(
    q,
  );
}

/** Extract the leading verb (case-insensitive) if the query starts with one. */
function leadingVerb(q: string): ActionVerb | null {
  const first = q.split(/\s+/)[0]?.toLowerCase();
  if (!first) return null;
  return (ACTION_VERBS as readonly string[]).includes(first)
    ? (first as ActionVerb)
    : null;
}

/** Pick N random items from an array (stable within one invocation). */
function pickRandom<T>(items: readonly T[], n: number): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    // noUncheckedIndexedAccess: swap in place with non-null assertions.
    const a = copy[i]!;
    const b = copy[j]!;
    copy[i] = b;
    copy[j] = a;
  }
  return copy.slice(0, Math.min(n, copy.length));
}

/**
 * Bilingual fuzzy entity match: Persian substring on the FA overlay OR
 * Latin substring (case-insensitive) on the canonical name. Capped to 8.
 */
function matchEntities(
  entities: readonly { name: string; label?: string }[],
  query: string,
): { name: string; label?: string }[] {
  const q = query.trim();
  if (!q) return [];
  const qLower = q.toLowerCase();
  const matches: { name: string; label?: string }[] = [];
  for (const e of entities) {
    const fa = ENTITY_NAME_FA[e.name];
    const latinHit = e.name.toLowerCase().includes(qLower);
    const faHit = fa ? fa.includes(q) : false;
    if (latinHit || faHit) matches.push(e);
    if (matches.length >= 8) break;
  }
  return matches;
}

/**
 * ⌘K command palette (frontend spec §5.1, §6.1).
 *
 * Four routing modes driven by input shape:
 *   1. `/` prefix        → navigation + settings command list
 *   2. verb prefix       → action dispatch (brief/summarize → dossier,
 *                          find/show/compare → NL question)
 *   3. plain text        → bilingual fuzzy entity match (EN ↔ FA)
 *   4. question-shaped   → "Ask" group running the full query
 * Empty query renders a rotating subset of suggested prompts.
 */
export function CommandPalette() {
  const { open, mode, close, setOpen } = useCommandPaletteStore();
  const [value, setValue] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const lang = useUiStore((s) => s.lang);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const setLang = useUiStore((s) => s.setLang);
  const { memberships, activeOrg, switchOrg } = useAuth();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  // Reset query when the palette closes so next open starts fresh.
  useEffect(() => {
    if (!open) setValue('');
  }, [open]);

  // Rotate the idle-state suggestions each time the palette opens.
  const idleSuggestions = useMemo(
    () => pickRandom(SUGGESTED_PROMPTS, 4),
    // re-pick each open; `open` is the desired trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  );

  const { data: graph } = useQuery({
    queryKey: ['graph', 'for-palette'],
    queryFn: () => api.graph(1000),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const entities = graph?.nodes ?? [];
  const query = value.trim();

  function runQuery(q: string) {
    navigate({ to: '/', search: { q } });
    close();
  }

  function openEntity(name: string) {
    navigate({ to: '/entity/$name', params: { name } });
    close();
  }

  async function addEntityToWatchlist(name: string) {
    await addWatchlistEntity(name);
    void queryClient.invalidateQueries({ queryKey: ['watchlist'] });
    close();
  }

  const onEntityPick = mode === 'add-to-watchlist' ? addEntityToWatchlist : openEntity;

  // Mode detection.
  const isCommandMode = query.startsWith('/');
  const verb = !isCommandMode ? leadingVerb(query) : null;
  const tail = verb ? query.slice(verb.length).trim() : '';
  const showQuestion = !isCommandMode && looksLikeQuestion(query);
  const entityMatches = !isCommandMode && !verb
    ? matchEntities(entities, query)
    : [];

  // Try to resolve the verb tail to a real entity (bilingual) for brief/summarize.
  const tailEntity = tail
    ? (matchEntities(entities, tail)[0]?.name ?? null)
    : null;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogContent hideClose className="p-0">
        <VisuallyHidden>
          <DialogTitle>Command palette</DialogTitle>
        </VisuallyHidden>
        <Command
          label="Command palette"
          className="divide-y divide-[var(--color-border)]"
          shouldFilter={false}
        >
          <div className="flex items-center gap-2 px-3">
            <Search className="size-4 text-[var(--color-ink-muted)]" />
            <Command.Input
              autoFocus
              value={value}
              onValueChange={setValue}
              placeholder={t('palette.placeholder', lang)}
              className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-[var(--color-ink-muted)]"
            />
          </div>

          <Command.List className="max-h-[60vh] overflow-y-auto p-2">
            <Command.Empty className="p-4 text-center text-xs text-[var(--color-ink-muted)]">
              {lang === 'fa'
                ? 'نتیجه‌ای یافت نشد. Enter را برای پرسیدن بزنید.'
                : 'No matches. Press Enter to ask as a question.'}
            </Command.Empty>

            {/* Idle: rotating suggested prompts. */}
            {query.length === 0 && (
              <Command.Group heading={t('palette.try', lang)}>
                {idleSuggestions.map((s) => (
                  <Item
                    key={s}
                    icon={<MessageSquare className="size-4" />}
                    onSelect={() => runQuery(s)}
                  >
                    {s}
                  </Item>
                ))}
              </Command.Group>
            )}

            {/* Mode 1: slash-prefixed command list. */}
            {isCommandMode && (
              <Command.Group heading={t('palette.commands', lang)}>
                <Item
                  icon={<Network className="size-4" />}
                  onSelect={() => {
                    navigate({ to: '/graph' });
                    close();
                  }}
                >
                  {t('palette.nav.graph', lang)}
                </Item>
                <Item
                  icon={<FolderOpen className="size-4" />}
                  onSelect={() => {
                    navigate({ to: '/investigations' });
                    close();
                  }}
                >
                  {t('palette.nav.dossiers', lang)}
                </Item>
                <Item
                  icon={<LayoutGrid className="size-4" />}
                  onSelect={() => {
                    navigate({ to: '/investigations' });
                    close();
                  }}
                >
                  {t('palette.nav.investigations', lang)}
                </Item>
                <Item
                  icon={<FolderPlus className="size-4" />}
                  onSelect={() => {
                    // Palette-level "new investigation" routes to the list page,
                    // which owns the create flow. Once the list page accepts a
                    // `?new=1` query param, this can route straight to the
                    // detail view of a freshly created doc.
                    navigate({ to: '/investigations' });
                    close();
                  }}
                >
                  {t('palette.cmd.newInvestigation', lang)}
                </Item>
                <Item
                  icon={<ScrollText className="size-4" />}
                  onSelect={() => {
                    navigate({ to: '/brief' });
                    close();
                  }}
                >
                  {t('palette.cmd.viewBriefs', lang)}
                </Item>
                <Item
                  icon={<Palette className="size-4" />}
                  onSelect={() => {
                    toggleTheme();
                    close();
                  }}
                >
                  {t('palette.cmd.theme', lang)}
                </Item>
                <Item
                  icon={<Languages className="size-4" />}
                  onSelect={() => {
                    setLang(lang === 'en' ? 'fa' : 'en');
                    close();
                  }}
                >
                  {t('palette.cmd.language', lang)}
                </Item>
                {memberships.length > 1 &&
                  memberships
                    .filter((m) => m.id !== activeOrg?.id)
                    .map((m) => (
                      <Item
                        key={m.id}
                        icon={<Building2 className="size-4" />}
                        onSelect={() => {
                          void switchOrg(m.id);
                          close();
                        }}
                      >
                        {t('org.switch', lang)} → {m.name}
                      </Item>
                    ))}
              </Command.Group>
            )}

            {/* Mode 2: verb-prefixed action dispatch. */}
            {verb && tail.length > 0 && (
              <Command.Group heading={t('palette.ask', lang)}>
                {(verb === 'brief' || verb === 'summarize') && (
                  <Item
                    icon={<FileText className="size-4" />}
                    onSelect={() =>
                      tailEntity ? openEntity(tailEntity) : runQuery(query)
                    }
                  >
                    {verb === 'brief'
                      ? `Brief on ${tailEntity ?? tail}`
                      : `Summarize ${tailEntity ?? tail}`}
                    <ArrowRight className="icon-directional ms-auto size-3.5 text-[var(--color-ink-muted)]" />
                  </Item>
                )}
                {verb === 'investigate' && (
                  <Item
                    icon={<FolderPlus className="size-4" />}
                    onSelect={() => {
                      // Routes to the investigations landing so the user can
                      // create a new case file. The tail is left on screen
                      // as context — the list page's "New investigation"
                      // action then creates the case file proper.
                      navigate({ to: '/investigations' });
                      close();
                    }}
                  >
                    {t('palette.verb.investigate', lang).replace(
                      '{target}',
                      tailEntity ?? tail,
                    )}
                    <ArrowRight className="icon-directional ms-auto size-3.5 text-[var(--color-ink-muted)]" />
                  </Item>
                )}
                {(verb === 'find' ||
                  verb === 'show' ||
                  verb === 'compare' ||
                  verb === 'trace') && (
                  <Item
                    icon={<Sparkles className="size-4" />}
                    onSelect={() => runQuery(query)}
                  >
                    {query}
                  </Item>
                )}
              </Command.Group>
            )}

            {/* Mode 3: plain-text bilingual entity match. */}
            {entityMatches.length > 0 && (
              <Command.Group
                heading={
                  mode === 'add-to-watchlist'
                    ? t('palette.add-to-watchlist', lang)
                    : t('palette.entities', lang)
                }
              >
                {entityMatches.map((e) => (
                  <Item
                    key={e.name}
                    icon={
                      mode === 'add-to-watchlist' ? (
                        <Eye className="size-4" />
                      ) : (
                        <FileText className="size-4" />
                      )
                    }
                    onSelect={() => void onEntityPick(e.name)}
                  >
                    <EntityName
                      name={e.name}
                      hideForeign={lang === 'en'}
                      className="text-sm"
                    />
                    {e.label && (
                      <span className="ms-auto text-xxs text-[var(--color-ink-muted)]">
                        {e.label}
                      </span>
                    )}
                  </Item>
                ))}
              </Command.Group>
            )}

            {/* Mode 4: question-shaped → Ask group. */}
            {showQuestion && !verb && (
              <Command.Group heading={t('palette.ask', lang)}>
                <Item
                  icon={<MessageSquare className="size-4" />}
                  onSelect={() => runQuery(query)}
                >
                  {query}
                </Item>
              </Command.Group>
            )}

            {/* AI refinement suggestions (existing). */}
            {!isCommandMode && (
              <CommandPaletteSuggestions
                query={query}
                lang={lang}
                onPick={(s) => runQuery(s)}
              />
            )}
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function Item({
  children,
  icon,
  onSelect,
}: {
  children: ReactNode;
  icon?: ReactNode;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-xs px-2 py-2 text-sm',
        'data-[selected=true]:bg-[var(--color-accent-soft)] data-[selected=true]:text-[var(--color-ink)]',
      )}
    >
      {icon && <span className="text-[var(--color-ink-muted)]">{icon}</span>}
      <span className="flex-1 truncate">{children}</span>
    </Command.Item>
  );
}
